import { useState } from "react";
import { ROLE_OPTIONS, formatDate, type Action } from "@aigov/domain";
import { api, download, type SystemDto, type SystemInput } from "../api";
import { CoverageCell, Pill, errMsg } from "../ui";

interface Props {
  tenantId: string;
  systems: SystemDto[] | null;
  allowed: (a: Action) => boolean;
  toast: (m: string) => void;
  onOpen: (id: string) => void;
  onCreated: (s: SystemDto) => void;
}

const NEW_SYSTEM: SystemInput = { name: "New AI system", ownerName: "", description: "", role: "deployer", jurisdictions: ["UK"], usStates: [], domain: "none", stage: "Idea", flags: {} };

export function RegisterView({ tenantId, systems, allowed, toast, onOpen, onCreated }: Props) {
  const [busy, setBusy] = useState(false);
  if (!systems) return <p className="muted">Loading the register…</p>;

  const highRisk = systems.filter((s) => s.summary.tier.label === "EU high-risk").length;
  const avg = systems.length ? Math.round(systems.reduce((a, s) => a + s.summary.coverage.pct, 0) / systems.length) : 0;
  const next = systems.map((s) => s.summary.nextDeadline).filter((d): d is string => !!d).sort()[0];
  const stale = systems.filter((s) => s.summary.stale).length;

  const add = async () => {
    setBusy(true);
    try { onCreated(await api<SystemDto>("POST", `/tenants/${tenantId}/systems`, NEW_SYSTEM)); }
    catch (e) { toast(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="stack">
      <div className="grid3">
        <div className="panel stat"><h3>Systems</h3><b>{systems.length}</b><span className="muted small">{stale ? `${stale} assessed against an older library` : "All on the current library"}</span></div>
        <div className="panel stat"><h3>EU high-risk</h3><b>{highRisk}</b><span className="muted small">Annex III from 2 Dec 2027</span></div>
        <div className="panel stat"><h3>Next deadline</h3><b>{next ? formatDate(next) : "None"}</b><span className="muted small">Average control coverage {avg}%</span></div>
      </div>
      <section className="panel">
        <div className="row spread" style={{ marginBottom: 10 }}>
          <h2>AI system register</h2>
          <div className="row">
            {allowed("export") && <button className="btn" onClick={() => download(`/tenants/${tenantId}/export.csv`, "register.csv").catch((e) => toast(errMsg(e)))}>Export CSV</button>}
            {allowed("system.write") && <button className="btn primary" disabled={busy} onClick={add}>Add system</button>}
          </div>
        </div>
        {systems.length ? (
          <div className="scroll"><table>
            <thead><tr><th>System</th><th>Supply</th><th>Jurisdictions</th><th>Classification</th><th>Coverage</th><th>Next deadline</th></tr></thead>
            <tbody>{systems.map((s) => (
              <tr key={s.id} className="click" tabIndex={0} onClick={() => onOpen(s.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(s.id)}>
                <td><strong>{s.name}</strong><div className="muted small">{s.stage}{s.ownerName && ` · ${s.ownerName}`}</div></td>
                <td className="small">{ROLE_OPTIONS.find((r) => r.value === s.role)?.short}</td>
                <td>{s.jurisdictions.map((j) => <span key={j} className="chip">{j}</span>)}</td>
                <td><Pill tone={s.summary.tier.tone}>{s.summary.tier.label}</Pill></td>
                <td><CoverageCell c={s.summary.coverage} /></td>
                <td className="small">{s.summary.nextDeadline ? formatDate(s.summary.nextDeadline) : <span className="muted">None upcoming</span>}</td>
              </tr>))}
            </tbody>
          </table></div>
        ) : (
          <div className="empty">
            <strong>No AI systems registered yet.</strong>
            <span className="muted">Add the first system. The tool works out which UK, EU, Australian and US obligations apply and which controls cover them.</span>
            {allowed("system.write") && <button className="btn primary" disabled={busy} onClick={add}>Add first system</button>}
          </div>
        )}
      </section>
    </div>
  );
}
